import { makeAutoObservable, runInAction } from "mobx";
import React from "react";
import SearchState, {
  API_BASE_URL,
  BASE_URL,
  DISABLE_CACHEING,
  SHORTENER_BASE_URL,
} from "../../SearchState";
import retryFetch from "../../utils/retryFetch";
import parseTrackCount from "../../utils/parseTrackCount";
import MusicTrackState from "../MusicTrack/MusicTrackState";

/** @typedef {import("@shared/musicEndpointTypes").DeletePlaylistPost} DeletePlaylistPost */
/** @typedef {import("@shared/musicEndpointTypes").DeletePlaylistPostResponse} DeletePlaylistPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").ListSavedPlaylistsGetResponse} ListSavedPlaylistsGetResponse */
/** @typedef {import("@shared/musicEndpointTypes").SavedPlaylistPost} SavedPlaylistPost */
/** @typedef {import("@shared/musicEndpointTypes").SavedSearchPostResponse} SavedSearchPostResponse */
/** @typedef {import("../../../sql/schemaDatatypes").SavedPlaylistSql} SavedPlaylistSql */

/**
 * @typedef {"notes" | "track count" | "created"} SortColumn
 */

/**
 * @typedef {"up" | "down" | null} SortDirection
 */

export default class MyAccountState {
  /** @type {SearchState} */
  searchState;

  /** @type {number | null} */
  showingPanelFirst = -1;

  /** @type {number | null} */
  overwritePlaylistId = null;

  /** @type {"list" | "save"} */
  currentPage = "list";

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
  }

  /**
   * Resets the search filter panel state
   * @action
   */
  resetSearchFilter = () => {
    this.showingPanelFirst = -1;
  };

  /**
   * Shows the search filter panel
   * @action
   */
  showSearchFilter = () => {
    this.showingPanelFirst = parseInt(this.showingPanelFirst, 2) + 1;
  };

  /**
   * Closes the account panel
   * @action
   */
  close = () => {
    this.filterPlaylists("");
    this.searchState.hideMyAccount();

    this.currentPage = "list";
    this.saveName = "";
    this.mergeIntoId = "";
  };

  logout = () => {
    retryFetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: "GET",
      mode: "cors",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then(() => {
        this.searchState.hideMyAccount();
        this.searchState.setLoggedInUser(null);
      });
  };

  // List page state

  /** @type {Array<SavedPlaylistSql>} */
  savedTracks = [];

  /** @type {SortColumn} */
  sortColumn = "notes";

  /** @type {SortDirection} */
  sortDirection = null;

  /** @type {string | null} */
  editPlaylistId = null;

  tempNote = "";

  filterSavedTerm = "";

  toList = () => {
    this.currentPage = "list";
  };

  /**
   * Filters playlists by search term
   * @param {string} term
   * @action
   */
  filterPlaylists = (term) => {
    if (term.length > 2) {
      this.filterSavedTerm = term;
    } else {
      this.filterSavedTerm = ``;
    }
  };

  /**
   * Starts editing a playlist note
   * @param {string} playlistId
   * @action
   */
  editNote = (playlistId) => {
    this.commitNoteEdit();
    this.editPlaylistId = playlistId;
    /** @type {SavedPlaylistSql | undefined} */
    const selectedPlaylist = this.savedTracks.filter(
      (p) => p.id === playlistId,
    )[0];
    if (selectedPlaylist != null) {
      if (
        selectedPlaylist.description === "none" ||
        selectedPlaylist.description === null
      ) {
        this.tempNote = "";
      } else {
        this.tempNote = selectedPlaylist.description;
      }
    }
  };

  regeneratePassword = () => {
    const randomstring = Math.random().toString(36).slice(-8);
    return randomstring;
  };

  editingPlaylistPassword = "";

  editingPlaylistPasswordId = -1;

  /**
   * Sets password for a playlist
   * @param {number} playlistId
   * @param {string} password
   * @action
   */
  setPasswordValue = (playlistId, password) => {
    this.editingPlaylistPasswordId = playlistId;
    this.editingPlaylistPassword = password;
  };

  savePassword = () => {
    if (this.editingPlaylistPasswordId == null) return;
    /** @type {SavedPlaylistSql | undefined} */
    const selectedPlaylist = this.savedTracks.filter(
      (p) => p.id === this.editingPlaylistPasswordId,
    )[0];
    if (selectedPlaylist != null) {
      selectedPlaylist.password = this.editingPlaylistPassword;

      /** @type {SavedPlaylistPost} */
      const savedPlaylist = {
        id: selectedPlaylist.id,
        password: this.editingPlaylistPassword,
        tracks: JSON.parse(selectedPlaylist.tracks),
      };

      retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
        method: "POST",
        cache: DISABLE_CACHEING ? "reload" : "default",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(savedPlaylist),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((r) => {
          if (r.id === -1) {
            alert(
              r.error === "not_logged_in"
                ? "You are not logged in. Please log in and try again."
                : "Failed to save. Please try again."
            );
            return;
          }
          if (r.id != null) {
            console.log(r);
            this.refreshSavedTracks();
          }
          // Do nothing
        });
    }
    this.editingPlaylistPasswordId = null;
  };

  commitNoteEdit = () => {
    if (this.editPlaylistId == null) return;
    /** @type {SavedPlaylistSql | undefined} */
    const selectedPlaylist = this.savedTracks.filter(
      (p) => p.id === this.editPlaylistId,
    )[0];
    if (selectedPlaylist != null) {
      // if (this.tempNote === '') this.tempNote = 'none';
      selectedPlaylist.description = this.tempNote;

      /** @type {SavedPlaylistPost} */
      const savedPlaylist = {
        id: selectedPlaylist.id,
        description: this.tempNote,
        // The server rejects playlist updates that have no tracks.
        tracks: JSON.parse(selectedPlaylist.tracks),
      };

      retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
        method: "POST",
        cache: DISABLE_CACHEING ? "reload" : "default",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(savedPlaylist),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((r) => {
          if (r.id === -1) {
            alert(
              r.error === "not_logged_in"
                ? "You are not logged in. Please log in and try again."
                : "Failed to save. Please try again."
            );
            return;
          }
          if (r.id != null) {
            console.log(r);
            this.refreshSavedTracks();
            this.searchState.logEvent("account", "edited-note", ``);
          }
          // Do nothing
        });
    }
    this.editPlaylistId = null;
  };

  /**
   * Sets the temporary note for editing
   * @param {string} tempNote
   * @action
   */
  setTempNote = (tempNote) => {
    this.tempNote = tempNote;
  };

  getDisplayName = () => {
    if (this.searchState.email != null) {
      return this.searchState.email;
    }
    return "My Tracks";
  };

  /**
   * Toggles the sort column and direction
   * @param {SortColumn} column
   * @action
   */
  toggleSortColumn = (column) => {
    if (this.sortColumn === column) {
      if (this.sortDirection == null) {
        this.sortDirection = "down";
      } else if (this.sortDirection === "down") {
        this.sortDirection = "up";
      } else if (this.sortDirection === "up") {
        this.sortDirection = null;
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = "down";
    }
  };

  /**
   * Sets the sort column and direction
   * @param {SortColumn} column
   * @param {SortDirection} direction
   * @action
   */
  setSortColumn = (column, direction) => {
    this.sortColumn = column;
    if (this.sortDirection === direction) {
      this.sortDirection = null;
    } else {
      this.sortDirection = direction;
    }
  };

  savedTracksOrganized = () => {
    let sortDirection = this.sortDirection;
    let sortColumn = this.sortColumn;
    if (this.sortDirection == null) {
      sortDirection = "down";
      sortColumn = "modified";
    }

    /** @type {Array<SavedPlaylistSql>} */
    const tracks = [].concat(this.savedTracks);
    tracks.sort((a, b) => {
      if (sortColumn === "track count") {
        const aCount = parseTrackCount(a.tracks, a.track_count);
        const bCount = parseTrackCount(b.tracks, b.track_count);
        if (sortDirection === "up") {
          return aCount - bCount;
        }
        if (sortDirection === "down") {
          return bCount - aCount;
        }
      } else if (sortColumn === "modified") {
        if (sortDirection === "up") {
          return parseInt(a.modified_on, 10) - parseInt(b.modified_on, 10);
        }
        if (sortDirection === "down") {
          return parseInt(b.modified_on, 10) - parseInt(a.modified_on, 10);
        }
      } else if (sortColumn === "track name") {
        if (sortDirection === "up") {
          return a.name.localeCompare(b.name, "en", { numeric: true });
        }
        if (sortDirection === "down") {
          return b.name.localeCompare(a.name, "en", { numeric: true });
        }
      }
      return 0;
    });
    if (this.filterSavedTerm !== ``) {
      /** @type {Array<MusicTrackState>} */
      const filteredTracks = tracks.filter((track) => {
        const name = track.name ? track.name : ``;
        const description = track.description ? track.description : ``;
        if (
          name.toLowerCase().includes(this.filterSavedTerm.toLowerCase()) ||
          description.toLowerCase().includes(this.filterSavedTerm.toLowerCase())
        ) {
          return track;
        }
        return false;
      });
      if (filteredTracks) return filteredTracks;
      return false;
    }
    return tracks;
  };

  refreshSavedTracks = () => {
    retryFetch(`${API_BASE_URL}/api/v1/list_saved_playlists`, {
      method: "GET",
      mode: "cors",
      cache: DISABLE_CACHEING ? "reload" : "default",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        runInAction(() => {
          if (!r.success) {
            this.savedTracks = [];
          } else {
            this.savedTracks = r.savedPlaylists;
          }
        });
      });
  };

  /**
   * Deletes a playlist after confirmation
   * @param {SavedPlaylistSql} playlist
   * @action
   */
  deletePlaylist = (playlist) => {
    this.searchState.showConfirmActionCallback(
      `Are you sure you want to delete "${playlist.name}"?`,
      () => {
        /** @type {DeletePlaylistPost} */
        const deletePlaylist = {
          id: playlist.id,
        };

        retryFetch(`${API_BASE_URL}/api/v1/delete_playlist`, {
          method: "POST",
          cache: DISABLE_CACHEING ? "reload" : "default",
          headers: new Headers({ "content-type": "application/json" }),
          body: JSON.stringify(deletePlaylist),
          mode: "cors",
          withCredentials: true,
          credentials: "include",
        })
          .then((r) => r.json())
          .then((r) => {
            if (r.success) {
              this.refreshSavedTracks();
              this.searchState.logEvent("account", "deleted-playlist", ``);
            }
          });
      },
    );
  };

  /**
   * Gets a short link for sharing a playlist
   * @param {string} id
   * @action
   */
  getShortLink = (id) => {
    retryFetch(`${SHORTENER_BASE_URL}/shorten/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url: `${BASE_URL}/en/favorites/${id}#likes=0&shared=1` }),
    })
      .then((r) => r.json())
      .then((r) => {
        this.searchState.shortLink = r.shortUrl;
        this.searchState.logEvent("account", "create-shortlink", "");
      });
  };

  /**
   * Downloads a playlist as a file
   * @param {SavedPlaylistSql} playlist
   * @action
   */
  downloadList = (playlist) => {
    console.log("Downloading list");
    // Even though each music.download() call also calls acceptTOS(), we wrap it here so that
    // if people reject the TOS, they don't have to reject it over and over again.
    this.searchState.acceptTOS(() => {
      console.log("Downloading list - TOS accepted");

      this.searchState.showMusicTypesPopup((type) => {
        console.log(`Downloading list - type = ${type}`);
        const link = `${API_BASE_URL}/api/v1/download_playlist?id=${playlist.id}&type=${type}`;
        /** @type {HTMLAnchorElement} */
        const linkElement = document.createElement("a");
        linkElement.href = link;
        linkElement.style.display = "none";
        linkElement.download = `${playlist.name}.zip`;
        if (document.body != null) {
          document.body.appendChild(linkElement);
          linkElement.click();
          linkElement.remove();
        }
      });
    });
  };

  duplicatePlaylist = (playlistId) => {
    console.log("Duplicating playlist");
    console.log(playlistId);

    retryFetch(`${API_BASE_URL}/api/v1/duplicate_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify({ playlistId }),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id != null) {
          this.refreshSavedTracks();
        } else {
          alert("Failed to duplicate playlist");
        }
      });
  };

  // Save page state

  saveName = "";

  mergeIntoId = "";

  /** @type {Array<string>} */
  savingIds = [];

  goToNewList = true;

  /** @type {NodeJS.Timeout | null} */
  viewSearchTimeout = null;

  /** @type {string | null} */
  editingPlaylistId = null;

  editingPlaylistName = "";

  /**
   * Views a saved search after a delay
   * @param {string} savedTrackId
   * @action
   */
  viewSearch = (savedTrackId) => {
    if (this.viewSearchTimeout == null) {
      this.viewSearchTimeout = setTimeout(() => {
        if (history != null) {
          history.pushState({}, "", `/en/favorites/${savedTrackId}`);
        }
        this.searchState.refreshSearchFromURL();
        this.close();
        this.viewSearchTimeout = null;
      }, 400);
    }
  };

  /**
   * Handles double-click on a saved search
   * @param {SavedPlaylistSql} savedPlaylist
   * @action
   */
  doubleClickSearch = (savedPlaylist) => {
    clearTimeout(this.viewSearchTimeout);
    this.viewSearchTimeout = null;
    this.editingPlaylistId = savedPlaylist.id;
    this.editingPlaylistName = savedPlaylist.name;
  };

  /**
   * Sets the name for editing a playlist
   * @param {string} name
   * @action
   */
  setEditingPlaylistName = (name) => {
    this.editingPlaylistName = name;
  };

  commitSearchRename = () => {
    for (let i = 0; i < this.savedTracks.length; i += 1) {
      if (this.savedTracks[i].id === this.editingPlaylistId) {
        this.savedTracks[i].name = this.editingPlaylistName;
        /** @type {SavedPlaylistPost} */
        const savedPlaylist = {
          id: this.editingPlaylistId,
          name: this.editingPlaylistName,
          tracks: JSON.parse(this.savedTracks[i].tracks),
        };

        retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
          method: "POST",
          cache: DISABLE_CACHEING ? "reload" : "default",
          headers: new Headers({ "content-type": "application/json" }),
          body: JSON.stringify(savedPlaylist),
          mode: "cors",
          withCredentials: true,
          credentials: "include",
        })
          .then((r) => r.json())
          .then((r) => {
            if (r.id === -1) {
              alert(
                r.error === "not_logged_in"
                  ? "You are not logged in. Please log in and try again."
                  : "Failed to save. Please try again."
              );
              return;
            }
            this.refreshSavedTracks();
            // Done
          });
      }
    }
    this.editingPlaylistId = null;
  };

  savePlaylistAfterTrackRemoval = () => {
    const trackIds = this.searchState.music.map((m) => String(m.music.id));

    /** @type {SavedPlaylistPost} */
    const savedPlaylist = {
      id: this.searchState.searchId,
      tracks: trackIds,
    };

    retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id === -1) {
          alert(
            r.error === "not_logged_in"
              ? "You are not logged in. Please log in and try again."
              : "Failed to save. Please try again."
          );
          return;
        }
        this.refreshSavedTracks();
        // Done
      });

    this.editingPlaylistId = null;
  };

  /**
   * Initiates saving a search with the given track IDs
   * @param {Array<string>} ids
   * @param {boolean} goToNewList
   * @action
   */
  toSaveSearch = (ids, goToNewList) => {
    this.currentPage = "save";
    this.savingIds = ids;
    this.goToNewList = goToNewList;
  };

  /**
   * Sets the name for saving a new search
   * @param {string} name
   * @action
   */
  setSaveName = (name) => {
    this.saveName = name;
    // Keep overwrite target in sync with current input; clear if no exact name match
    const matching = this.savedTracks.find(p => p.name === name);
    this.overwritePlaylistId = matching ? matching.id : null;
  };

  overwritePlaylistName = (id) => {
    this.overwritePlaylistId = id;
  };

  nullifyOverwritePlaylistId = () => {
    this.overwritePlaylistId = null;
  };

  /**
   * Sets the ID of playlist to merge into
   * @param {string} id
   * @action
   */
  setMergeIntoId = (id) => {
    this.mergeIntoId = id;
  };

  saveNewSearch = () => {
    if (this.saveName === "") {
      alert("We can't save a track without a name.");
      return;
    }

    /** @type {SavedPlaylistPost} */
    const savedPlaylist = {
      tracks: this.savingIds,
      name: this.saveName,
    };

    if (this.overwritePlaylistId) {
      const overwrite = this.savedTracks.find(p => p.id === this.overwritePlaylistId);
      if (overwrite && overwrite.name === this.saveName) {
        savedPlaylist.id = this.overwritePlaylistId;
      }
      this.overwritePlaylistId = null;
    }

    retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id === -1) {
          alert(
            r.error === "not_logged_in"
              ? "You are not logged in. Please log in and try again."
              : "Failed to save. Please try again."
          );
          return;
        }
        this.refreshSavedTracks();
        this.close();
        if (this.goToNewList) {
          const url = `/en/favorites/${r.id}`;
          window.history.pushState({}, "", url);
          this.searchState.refreshSearchFromURL();
          this.searchState.logEvent("account", "save-search", ``);
        }
      });
  };

  /**
   * Adds tracks to an existing playlist
   * @param {SavedPlaylistSql} playlist
   * @param {string[]} newTrackIds
   * @action
   */
  addTrackToPlaylist = (playlist, newTrackIds) => {
    /** @type {SavedPlaylistPost} */
    const savedPlaylist = {
      id: playlist.id,
      tracks: JSON.parse(playlist.tracks).concat(newTrackIds),
    };
    console.log(`Old tracks: ${JSON.stringify(playlist.tracks)}`);
    console.log(`New track IDs: ${JSON.stringify(newTrackIds)}`);
    console.log(JSON.stringify(savedPlaylist, null, 2));

    retryFetch(`${API_BASE_URL}/api/v1/saved_playlist`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(savedPlaylist),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.id === -1) {
          alert(
            r.error === "not_logged_in"
              ? "You are not logged in. Please log in and try again."
              : "Failed to save. Please try again."
          );
          return;
        }
        this.refreshSavedTracks();
        this.searchState.logEvent("account", "playlist-add-track", ``);
        console.log(r);
      });
  };

  mergeWithOtherSearch = () => {
    if (this.mergeIntoId === "") {
      alert(
        "We can't merge a track until you specify what track to merge with.",
      );
      return;
    }

    const formData = new FormData();
    formData.append("playlistId", this.mergeIntoId);
    for (let i = 0; i < this.searchState.music.length; i += 1) {
      const track = this.searchState.music[i];
      formData.append("tracks[]", track.music.id);
    }

    const headers = new Headers({
      // Need this version to support localhost
      X_REQUESTED_WITH: "XMLHttpRequest",
      // Need this version to support Apache
      "X-REQUESTED-WITH": "XMLHttpRequest",
    });

    retryFetch(`${BASE_URL}/account/add-tracks-to-playlist`, {
      method: "POST",
      body: formData,
      mode: "cors",
      redirect: "error",
      withCredentials: true,
      credentials: "include",
      headers,
    })
      .then((r) => r.text())
      .then(() => {
        if (window != null) {
          window.history.pushState({}, "", `/en/favorites/${this.mergeIntoId}`);
        }
        this.searchState.refreshSearchFromURL();
        this.close();
        this.searchState.logEvent("account", "playlist-merged", ``);
      });
  };
}
